import { addWeeks, lastDayOfMonth, setDay, startOfMonth, subWeeks } from 'date-fns'

export const isStopMatch = (stop, targetId) => {
  if (!stop || !targetId) return false
  const strTarget = String(targetId)
  return String(stop.id) === strTarget || String(stop.workOrderId) === strTarget || (stop.stopId && String(stop.stopId) === strTarget)
}

const daysOfWeekMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const weekDaysCache = new Map()

export const getScheduledDaysForService = ({ service, startDate, endDate, context }) => {
  const { daysOfWeek, daysOfMonth, daysOfWeekInMonth } = service
  if (!daysOfWeek?.length && !daysOfMonth?.length && !daysOfWeekInMonth?.length) return []

  const scheduledDays = new Set()

  // Process daysOfWeek
  if (Array.isArray(daysOfWeek)) {
    daysOfWeek.forEach(day => scheduledDays.add(day))
  }

  // Pre-calculate common values for this week/month context or use provided context
  const year = context?.year ?? startDate.getFullYear()
  const monthIndex = context?.monthIndex ?? startDate.getMonth()
  const lastDay = context?.lastDay ?? lastDayOfMonth(startDate)
  const startTime = context?.startTime ?? startDate.getTime()
  const endTime = context?.endTime ?? endDate.getTime()

  // Process daysOfMonth
  if (Array.isArray(daysOfMonth) && daysOfMonth.length > 0) {
    daysOfMonth.forEach(dayNumber => {
      let date = new Date(year, monthIndex, dayNumber)
      if (date.getMonth() !== monthIndex) {
        date = lastDay
      }

      const dateTime = date.getTime()
      if (dateTime >= startTime && dateTime <= endTime) {
        scheduledDays.add(daysOfWeekMap[date.getDay()])
      }
    })
  }

  // Process daysOfWeekInMonth
  if (Array.isArray(daysOfWeekInMonth) && daysOfWeekInMonth.length > 0) {
    const monthsToCheck = [startDate]
    if (startDate.getMonth() !== endDate.getMonth()) {
      monthsToCheck.push(endDate)
    }

    daysOfWeekInMonth.forEach(({ day, occurrence }) => {
      const targetDayIndex = daysOfWeekMap.indexOf(day)
      monthsToCheck.forEach(dateInMonth => {
        const firstDay = startOfMonth(dateInMonth)
        const lastDayInMonth = lastDayOfMonth(dateInMonth)
        let calculatedDate = null

        if (occurrence === -1) {
          calculatedDate = setDay(lastDayInMonth, targetDayIndex, { weekStartsOn: 0 })
          if (lastDayInMonth.getMonth() !== calculatedDate.getMonth()) {
            calculatedDate = subWeeks(calculatedDate, 1)
          }
        } else {
          let firstDayInMonth = setDay(firstDay, targetDayIndex, { weekStartsOn: 0 })
          if (firstDayInMonth.getMonth() !== firstDay.getMonth()) {
            firstDayInMonth = addWeeks(firstDayInMonth, 1)
          }
          calculatedDate = addWeeks(firstDayInMonth, occurrence - 1)
          if (calculatedDate.getMonth() !== firstDay.getMonth()) {
            calculatedDate = null
          }
        }

        if (calculatedDate) {
          const dateTime = calculatedDate.getTime()
          if (dateTime >= startTime && dateTime <= endTime) {
            scheduledDays.add(daysOfWeekMap[calculatedDate.getDay()])
          }
        }
      })
    })
  }

  const resultArray = Array.from(scheduledDays).sort((a, b) => daysOfWeekMap.indexOf(a) - daysOfWeekMap.indexOf(b))
  const cacheKey = resultArray.join(',')
  if (!weekDaysCache.has(cacheKey)) {
    weekDaysCache.set(cacheKey, resultArray)
  }
  return weekDaysCache.get(cacheKey)
}

export const resolveIds = (val, options = []) => {
  if (!val) return []
  const arr = Array.isArray(val) ? val : [val]

  return arr
    .map(v => {
      let valueToMatch = v

      if (typeof v === 'object' && v !== null) {
        if (v.id) return v.id
        valueToMatch = v.value || v.label || v
      }

      const optionMatch = options.find(
        o =>
          String(o.label).trim().toLowerCase() === String(valueToMatch).trim().toLowerCase() ||
          String(o.value).trim().toLowerCase() === String(valueToMatch).trim().toLowerCase()
      )

      if (optionMatch) {
        return optionMatch.id
      }

      // Return null if no match found - will be filtered out
      return null
    })
    .filter(v => v !== null && typeof v !== 'object')
}
